import { Link } from 'react-router-dom';
import { Cloud, Brain, Shield, Settings, Database, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

export default function AboutPage() {
  const expertiseCategories = [
    {
      title: 'Cloud Infrastructure & Architecture',
      icon: Cloud,
      items: [
        'Multi-cloud strategy (AWS, Azure, GCP)',
        'Hybrid cloud architecture',
        'Cloud cost optimization (clients typically see 40-70% savings)',
        'Infrastructure as Code (Terraform, CloudFormation)',
        'Migration strategy and execution',
      ],
    },
    {
      title: 'AI/ML Infrastructure',
      icon: Brain,
      items: [
        'Multi-model LLM deployment and orchestration',
        'AI governance and observability',
        'Model serving infrastructure',
        'Cost-optimized inference strategies',
        'RAG architecture and vector databases',
      ],
    },
    {
      title: 'DevSecOps & SRE',
      icon: Shield,
      items: [
        'CI/CD pipeline design and implementation',
        'Container orchestration (Kubernetes, EKS, AKS, GKE)',
        'Security automation and compliance',
        'Observability and monitoring (Datadog, New Relic, Prometheus)',
        'Incident response and reliability engineering',
      ],
    },
  ];

  const approachSteps = [
    {
      number: '01',
      title: 'Business Context First',
      description: 'Start with business goals, ROI.',
    },
    {
      number: '02',
      title: 'Pragmatic, Not Perfect',
      description: 'Ship working solutions in 90 days, iterate.',
    },
    {
      number: '03',
      title: 'Data-Driven Decisions',
      description: 'Quantitative analysis, measure, forecast, validate.',
    },
    {
      number: '04',
      title: 'Knowledge Transfer',
      description: 'Team owns the solution, train as I build.',
    },
    {
      number: '05',
      title: 'Long-Term Thinking',
      description: 'Design for 3-5 years, avoid technical debt.',
    },
  ];

  const speakingTopics = [
    'Enterprise AI infrastructure strategy',
    'Multi-cloud architecture patterns',
    'Cost optimization in cloud',
    'DevSecOps transformation',
    'SRE best practices',
  ];

  return (
    <main className="pt-20" data-testid="about-page">
      <SEO
        title="About Siddharth Kaul | Cloud Infrastructure Expert | Fortune 500 Consulting"
        description="Learn about Siddharth Kaul, Associate Director of Cloud & AI Infrastructure with extensive experience helping Fortune 500 companies with multi-cloud migrations, AI/ML platforms, and DevSecOps transformations."
        keywords="cloud infrastructure expert, AI infrastructure consultant, DevSecOps transformation, multi-cloud migration, Kubernetes expert, SRE best practices"
        canonicalUrl="/about"
      />
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm">
                About Me
              </p>
              <h1 className="font-['Montserrat'] font-extrabold text-4xl md:text-5xl text-[#2C3E50] leading-tight">
                Background & Expertise
              </h1>
              <p className="text-lg text-[#64748B] leading-relaxed">
                I'm Siddharth Kaul, Associate Director of Cloud and AI Infrastructure, SRE & DevSecOps, with extensive experience helping Fortune 500 companies transform technology infrastructure.
              </p>
              <p className="text-[#64748B] leading-relaxed">
                I solve complex infrastructure challenges at enterprise scale—multi-cloud migrations, AI/ML platform deployments, and DevSecOps transformations.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#3498DB]/10 rounded-2xl blur-xl" />
              <img
                src="https://customer-assets.emergentagent.com/job_e450bca2-37df-4eee-b764-211a4cfae415/artifacts/my5izqnl_linkedin.png"
                alt="Siddharth Kaul"
                className="relative w-full h-[400px] object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 md:py-28 bg-[#F8F9FA]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
              Expertise
            </p>
            <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl text-[#2C3E50]">
              Areas of Expertise
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {expertiseCategories.map((category, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 border border-slate-200 card-hover"
                data-testid={`expertise-card-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-[#3498DB]/10 flex items-center justify-center mb-6">
                  <category.icon className="text-[#3498DB]" size={24} />
                </div>
                <h3 className="font-['Montserrat'] font-bold text-lg text-[#2C3E50] mb-4">
                  {category.title}
                </h3>
                <ul className="space-y-3">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#64748B]">
                      <CheckCircle size={16} className="text-[#27AE60] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm">
                Philosophy
              </p>
              <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl text-[#2C3E50]">
                How I Work
              </h2>
              <p className="text-lg text-[#64748B] leading-relaxed">
                Infrastructure decisions driven by business outcomes.
              </p>
            </div>

            <div className="space-y-6">
              {approachSteps.map((step, index) => (
                <div
                  key={index}
                  className="flex gap-4 p-4 rounded-lg hover:bg-[#F8F9FA] transition-colors duration-200"
                  data-testid={`approach-step-${index}`}
                >
                  <span className="text-[#3498DB] font-['Montserrat'] font-bold text-2xl">
                    {step.number}
                  </span>
                  <div>
                    <h4 className="font-['Montserrat'] font-semibold text-[#2C3E50] mb-1">
                      {step.title}
                    </h4>
                    <p className="text-[#64748B] text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Speaking Section */}
      <section className="py-20 md:py-28 bg-[#2C3E50]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm">
                Speaking
              </p>
              <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl text-white">
                Speaking & Publications
              </h2>
              <p className="text-slate-300 leading-relaxed">
                Available for keynotes, panels, and workshops (virtual or in-person).
              </p>

              <div className="space-y-3">
                <p className="text-white font-semibold">Topics:</p>
                <ul className="space-y-2">
                  {speakingTopics.map((topic, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-300">
                      <span className="text-[#3498DB]">•</span>
                      {topic}
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                asChild
                className="bg-[#3498DB] hover:bg-[#2980B9] text-white px-6 py-5"
              >
                <Link to="/contact">
                  Contact for Inquiries
                  <ArrowRight size={16} className="ml-2" />
                </Link>
              </Button>
            </div>

            <div className="bg-white/10 rounded-xl p-8">
              <h3 className="font-['Montserrat'] font-bold text-lg text-white mb-4">
                Recent Publications
              </h3>
              <div className="space-y-4">
                <div className="p-4 bg-white/5 rounded-lg">
                  <p className="text-white font-medium mb-1">
                    Multi-Model LLM Architecture: The Kubernetes Pattern in Enterprise AI Infrastructure
                  </p>
                  <p className="text-slate-400 text-sm">December 2025</p>
                </div>
                <p className="text-slate-400 text-sm italic">
                  More publications coming soon...
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl text-[#2C3E50] mb-6">
            Ready to Transform Your Infrastructure?
          </h2>
          <p className="text-lg text-[#64748B] mb-8 max-w-2xl mx-auto">
            Let's discuss how I can help your organization achieve its infrastructure goals.
          </p>
          <Button
            asChild
            className="bg-[#3498DB] hover:bg-[#2980B9] text-white px-8 py-6 text-base shadow-[0_4px_14px_0_rgba(52,152,219,0.39)]"
          >
            <Link to="/contact">
              Book a Consultation
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </main>
  );
}
